import { useState } from 'react';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useGetCase } from '@/api/queries/useGetCase';
import { useGetDocSubsets } from '@/api/queries/useGetDocSubsets';
import { DocSubset } from '@/api/types/backend-dto-types';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { SearchInput } from '@/new/components/search-input';
import { Button } from '@/new/components/shadcn/button';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/new/components/shadcn/table';
import { CellWithCopy } from '@/new/components/table-cell-with-copy';
import { TableFooter } from '@/new/components/table-footer';

type DocSubsetWithDocCount = DocSubset & { docCount: number };

const fallbackTableData: DocSubsetWithDocCount[] = [];
const columnHelper = createColumnHelper<DocSubsetWithDocCount>();

const columns = [
  columnHelper.accessor('title', {
    header: () => <span>Subset Name</span>,
    cell: (info) => <Link to={info.row.original.id}>{info.getValue()}</Link>,
  }),
  columnHelper.accessor('id', {
    header: () => <span>Subset Id</span>,
    cell: (info) => (
      <CellWithCopy textToCopy={info.getValue()}>
        <span className="line-clamp-1 max-w-24 break-all">{info.getValue()}</span>
      </CellWithCopy>
    ),
  }),
  columnHelper.accessor('createdBy.email', {
    header: () => <span>Created By</span>,
  }),
  columnHelper.accessor('docCount', {
    header: () => <span>No. of Docs</span>,
  }),
];

export function Subsets() {
  const { matterId } = useParams();
  if (!matterId) return;

  const [globalFilter, setGlobalFilter] = useState('');

  const navigate = useNavigate();

  const { data: matter } = useGetCase(matterId);
  const { data: subsets } = useGetDocSubsets(matterId);

  // Separate isAllDocuments subset from regular subsets
  const allDocumentsSubset = subsets?.find((s) => s.isAllDocuments);
  const regularSubsets = subsets?.filter((s) => !s.isAllDocuments) ?? fallbackTableData;

  const table = useReactTable({
    data: regularSubsets,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [
        {
          id: 'docCount',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 10,
      },
    },
    state: {
      globalFilter,
    },
  });

  return (
    <>
      <Breadcrumb
        items={
          matter
            ? [
                { href: '/matters', label: 'Matters', active: false },
                { href: `/matters/${matterId}`, label: matter.name, active: false },
                { href: `/matters/${matterId}/subsets`, label: 'Subsets', active: true },
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />

      <PageContainer>
        <PageHeader>
          <PageTitle
            text="Subsets"
            subtext="Use subsets to group documents into smaller collections, to view data just from those documents."
          />

          <Button variant="primary" asChild>
            <Link to="new">
              <Icon className="lucide--group" />
              <span>Create a New Subset</span>
            </Link>
          </Button>
        </PageHeader>

        {allDocumentsSubset && (
          <Link to={allDocumentsSubset.id}>
            <div className="group flex cursor-pointer items-center justify-between rounded-md border border-gray-200 bg-gradient-to-l from-gray-50 to-white p-4 transition-all hover:border-gray-300">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100">
                  <Icon className="lucide--group text-brandPrimary text-xl" />
                </div>
                <div className="flex flex-col gap-1">
                  <h1 className="font-serif text-lg font-semibold text-gray-800">{allDocumentsSubset.title}</h1>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Icon className="lucide--file-text text-base" />
                    <span>View data from all {allDocumentsSubset.docCount} documents in this matter.</span>
                  </div>
                </div>
              </div>
              <Icon className="lucide--chevron-right text-xl text-gray-400 transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        )}

        <SearchInput
          placeholder="Search subsets..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="my-6 max-w-sm"
        />

        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} onClick={() => navigate(row.original.id)} className="cursor-pointer">
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TableFooter table={table} plural="subsets" />
      </PageContainer>
    </>
  );
}
